/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPublishedProfileDoc } from '@/features/studio/api/profileStudio';
import {
  compileProfileTheme,
  defaultProfileDoc,
  type CompiledProfileTheme,
  type ProfileDoc,
} from '@/features/studio/model/profileDoc';

/**
 * Published profile customization for a user, resolved for the real surfaces
 * (ProfilePreviewCard, UserProfileModal, FriendshipInfoRail). Inside WINR
 * Studio previews, <ProfileThemePreview> overrides the published doc with the
 * live draft — the preview components are the production components, fed the
 * unpublished document through this exact same hook.
 */

export const profileThemeKeys = {
  published: (userId: string) => ['profile-theme', userId] as const,
};

const PreviewContext = createContext<ProfileDoc | null>(null);

/** Studio-only wrapper: forces every surface below it to render `doc` (the draft). */
export function ProfileThemePreview({ doc, children }: { doc: ProfileDoc; children: ReactNode }) {
  return <PreviewContext.Provider value={doc}>{children}</PreviewContext.Provider>;
}

export function useProfileTheme(
  userId: string | null | undefined,
  fallbackAccent: string,
): CompiledProfileTheme {
  const preview = useContext(PreviewContext);
  const { data } = useQuery({
    queryKey: profileThemeKeys.published(userId ?? ''),
    queryFn: () => getPublishedProfileDoc(userId!),
    enabled: Boolean(userId) && !preview,
    staleTime: 60_000,
  });
  const doc = preview ?? data ?? defaultProfileDoc();
  return compileProfileTheme(doc, fallbackAccent);
}
